import { useState, useEffect, useRef } from 'react';
import { useParams, Link } from 'react-router-dom';
import { Card, CardContent } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Textarea } from '@/components/ui/textarea';
import { driverAPI, serviceLogsAPI, uploadAPI, issueReportsAPI } from '@/lib/api';
import { useAuth } from '@/lib/auth';
import { getJobTypeColor, getStatusColor } from '@/lib/utils';
import { toast } from 'sonner';
import { 
  Loader2, 
  MapPin, 
  Package,
  CheckCircle2,
  AlertTriangle,
  XCircle,
  ArrowLeft,
  Truck,
  Clock,
  ChevronDown,
  ChevronUp,
  Camera,
  Image,
  X,
  Upload,
  Navigation,
  ExternalLink,
  Wrench,
  Ban,
  MessageSquareWarning,
  ShieldAlert
} from 'lucide-react';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from '@/components/ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Label } from '@/components/ui/label';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

export default function DriverView() {
  const { driverId } = useParams();
  const { user } = useAuth();
  const [loading, setLoading] = useState(true);
  const [driverData, setDriverData] = useState(null);
  const [expandedJob, setExpandedJob] = useState(null);
  const [notes, setNotes] = useState({});
  const [photos, setPhotos] = useState({});
  const [uploading, setUploading] = useState(null);
  const [updating, setUpdating] = useState(null);
  const fileInputRefs = useRef({});
  
  // Issue reporting state
  const [showIssueDialog, setShowIssueDialog] = useState(false);
  const [issueJob, setIssueJob] = useState(null);
  const [issueType, setIssueType] = useState('');
  const [issueDescription, setIssueDescription] = useState('');
  const [issuePhotos, setIssuePhotos] = useState([]);
  const [submittingIssue, setSubmittingIssue] = useState(false);

  useEffect(() => {
    fetchDriverRoute();
  }, [driverId]);

  const fetchDriverRoute = async () => {
    try {
      const response = await driverAPI.getTodayRoute(driverId);
      setDriverData(response.data);
    } catch (error) {
      console.error('Error fetching driver route:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleFileSelect = async (jobId, event) => {
    const files = Array.from(event.target.files);
    if (files.length === 0) return;

    setUploading(jobId);
    const uploadedUrls = [];

    try {
      for (const file of files) {
        const response = await uploadAPI.uploadPhoto(file);
        uploadedUrls.push(response.data.url);
      }

      setPhotos(prev => ({
        ...prev,
        [jobId]: [...(prev[jobId] || []), ...uploadedUrls]
      }));

      toast.success(`${files.length} photo(s) uploaded`);
    } catch (error) {
      console.error('Error uploading photos:', error);
      toast.error('Failed to upload photos');
    } finally {
      setUploading(null);
    }
  };

  const handleCameraCapture = async (jobId, event) => {
    const file = event.target.files?.[0];
    if (!file) return;

    setUploading(jobId);

    try {
      const response = await uploadAPI.uploadPhoto(file);
      setPhotos(prev => ({
        ...prev,
        [jobId]: [...(prev[jobId] || []), response.data.url]
      }));
      toast.success('Photo captured');
    } catch (error) {
      console.error('Error uploading photo:', error);
      toast.error('Failed to upload photo');
    } finally {
      setUploading(null);
    }
  };

  const removePhoto = (jobId, photoIndex) => {
    setPhotos(prev => ({
      ...prev,
      [jobId]: prev[jobId].filter((_, idx) => idx !== photoIndex)
    }));
  };

  const handleStatusUpdate = async (job, status) => {
    setUpdating(job.id);
    try {
      const jobPhotos = photos[job.id] || [];
      
      await serviceLogsAPI.create({
        job_id: job.id,
        driver_id: driverId,
        service_date_time: new Date().toISOString(),
        status: status,
        notes: notes[job.id] || '',
        photos: jobPhotos
      });
      
      toast.success(`Stop marked as ${status.replace('_', ' ')}`);
      fetchDriverRoute();
      setExpandedJob(null);
      setNotes(prev => ({ ...prev, [job.id]: '' }));
      setPhotos(prev => ({ ...prev, [job.id]: [] }));
    } catch (error) {
      console.error('Error updating status:', error);
      toast.error('Failed to update status');
    } finally {
      setUpdating(null);
    }
  };

  // Open issue report dialog
  const openIssueDialog = (job, presetType = '') => {
    setIssueJob(job);
    setIssueType(presetType);
    setIssueDescription('');
    setIssuePhotos([...photos[job.id] || []]);
    setShowIssueDialog(true);
    setExpandedJob(null);
  };

  // Handle issue photo upload
  const handleIssuePhotoCapture = async (event) => {
    const file = event.target.files?.[0];
    if (!file) return;

    try {
      const response = await uploadAPI.uploadPhoto(file);
      setIssuePhotos(prev => [...prev, response.data.url]);
      toast.success('Photo added');
    } catch (error) {
      console.error('Error uploading photo:', error);
      toast.error('Failed to upload photo');
    }
  };

  const removeIssuePhoto = (index) => {
    setIssuePhotos(prev => prev.filter((_, i) => i !== index));
  };

  // Submit issue report
  const handleSubmitIssue = async () => {
    if (!issueType) {
      toast.error('Please select an issue type');
      return;
    }

    setSubmittingIssue(true);
    try {
      // Create the issue report
      await issueReportsAPI.create({
        job_id: issueJob.id,
        driver_id: driverId,
        issue_type: issueType,
        unit_id: null, // Could be enhanced to allow unit selection
        description: issueDescription,
        photos: issuePhotos
      });

      // Also create a service log to mark the job
      await serviceLogsAPI.create({
        job_id: issueJob.id,
        driver_id: driverId,
        service_date_time: new Date().toISOString(),
        status: issueType === 'not_accessible' ? 'not_accessible' : 'issue',
        notes: `Issue reported: ${issueType}. ${issueDescription}`,
        photos: issuePhotos
      });

      toast.success('Issue reported! Dispatch has been notified.', {
        duration: 4000,
        icon: '📢'
      });
      
      setShowIssueDialog(false);
      setIssueJob(null);
      setIssueType('');
      setIssueDescription('');
      setIssuePhotos([]);
      fetchDriverRoute();
    } catch (error) {
      console.error('Error submitting issue:', error);
      toast.error('Failed to submit issue report');
    } finally {
      setSubmittingIssue(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-slate-50 flex items-center justify-center">
        <Loader2 className="h-8 w-8 animate-spin text-[#194461]" />
      </div>
    );
  }

  const driver = driverData?.driver;
  const route = driverData?.route;
  const stops = driverData?.stops || [];

  return (
    <div className="min-h-screen bg-slate-50">
      {/* Header */}
      <div className="bg-[#194461] text-white sticky top-0 z-50">
        <div className="px-4 py-4">
          <div className="flex items-center justify-between">
            <Link to="/admin/dashboard" className="flex items-center gap-2 text-white/80 hover:text-white">
              <ArrowLeft size={20} />
              <span className="text-sm">Back</span>
            </Link>
            <div className="flex items-center gap-2">
              <img src="/logo-icon.svg" alt="Tinkle Tent" className="h-8 w-8 brightness-0 invert" />
              <span className="font-bold text-white">Tinkle Tent</span>
            </div>
          </div>
          
          <div className="mt-4">
            <p className="text-white/70 text-sm">Today's Route</p>
            <h1 className="text-xl font-bold" data-testid="driver-name">{driver?.full_name}</h1>
            {route ? (
              <p className="text-white/80 text-sm mt-1">{route.name}</p>
            ) : (
              <p className="text-white/60 text-sm mt-1">No route assigned</p>
            )}
          </div>

          {/* Progress */}
          {stops.length > 0 && (
            <div className="mt-4">
              <div className="flex items-center justify-between text-sm mb-2">
                <span>Progress</span>
                <span>{stops.filter(s => s.status === 'completed').length} / {stops.length} complete</span>
              </div>
              <div className="h-2 bg-white/20 rounded-full overflow-hidden">
                <div 
                  className="h-full bg-[#52B8E1] transition-all"
                  style={{ width: `${(stops.filter(s => s.status === 'completed').length / stops.length) * 100}%` }}
                />
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Stops List */}
      <div className="p-4 pb-24">
        {!route || stops.length === 0 ? (
          <Card className="border-slate-200">
            <CardContent className="p-8 text-center">
              <Truck className="mx-auto text-slate-300 mb-4" size={48} />
              <h3 className="text-lg font-semibold text-slate-900 mb-2">No Stops Today</h3>
              <p className="text-slate-600">You don't have any stops scheduled for today.</p>
            </CardContent>
          </Card>
        ) : (
          <div className="space-y-4">
            {stops.map((job, index) => {
              const isExpanded = expandedJob === job.id;
              const isCompleted = job.status === 'completed';
              const jobPhotos = photos[job.id] || [];
              
              return (
                <Card 
                  key={job.id}
                  className={`border-slate-200 transition-all ${isCompleted ? 'bg-emerald-50 border-emerald-200' : ''}`}
                  data-testid={`stop-card-${job.id}`}
                >
                  <CardContent className="p-0">
                    {/* Main content - clickable */}
                    <button
                      onClick={() => setExpandedJob(isExpanded ? null : job.id)}
                      className="w-full p-4 text-left"
                    >
                      <div className="flex items-start gap-4">
                        {/* Stop number */}
                        <div className={`w-10 h-10 rounded-full flex items-center justify-center flex-shrink-0 font-bold ${
                          isCompleted 
                            ? 'bg-emerald-500 text-white' 
                            : 'bg-slate-200 text-slate-600'
                        }`}>
                          {isCompleted ? <CheckCircle2 size={20} /> : index + 1}
                        </div>

                        <div className="flex-1 min-w-0">
                          <div className="flex items-center gap-2 flex-wrap mb-2">
                            <Badge className={getJobTypeColor(job.job_type)} variant="outline">
                              {job.job_type}
                            </Badge>
                            <Badge className={getStatusColor(job.status)} variant="outline">
                              {job.status.replace('_', ' ')}
                            </Badge>
                          </div>

                          <h3 className="font-semibold text-slate-900 mb-1">{job.customer_name}</h3>
                          
                          <div className="flex items-start gap-1 text-sm text-slate-600">
                            <MapPin size={14} className="mt-0.5 flex-shrink-0" />
                            <span className="line-clamp-2">{job.site_address}</span>
                          </div>

                          {job.scheduled_time_window && (
                            <div className="flex items-center gap-1 text-sm text-slate-500 mt-1">
                              <Clock size={14} />
                              <span>{job.scheduled_time_window}</span>
                            </div>
                          )}

                          <div className="flex items-center gap-1 text-sm text-slate-500 mt-1">
                            <Package size={14} />
                            <span>{job.units_requested} {job.unit_type}</span>
                          </div>
                        </div>

                        <div className="text-slate-400">
                          {isExpanded ? <ChevronUp size={20} /> : <ChevronDown size={20} />}
                        </div>
                      </div>
                    </button>

                    {/* Quick Navigate Button - Always visible for incomplete stops */}
                    {!isCompleted && !isExpanded && (
                      <div className="px-4 pb-3 flex gap-2">
                        <a 
                          href={`https://www.google.com/maps/dir/?api=1&destination=${encodeURIComponent(job.site_address)}`}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="flex-1"
                          onClick={(e) => e.stopPropagation()}
                        >
                          <Button 
                            variant="outline" 
                            className="w-full h-11 text-[#194461] border-[#194461]/30 hover:bg-[#194461]/5"
                            data-testid={`navigate-btn-${job.id}`}
                          >
                            <Navigation size={18} className="mr-2" />
                            Get Directions
                          </Button>
                        </a>
                      </div>
                    )}

                    {/* Expanded content */}
                    {isExpanded && !isCompleted && (
                      <div className="px-4 pb-4 pt-2 border-t border-slate-100">
                        {/* Navigation buttons when expanded */}
                        <div className="grid grid-cols-2 gap-2 mb-4">
                          <a 
                            href={`https://www.google.com/maps/dir/?api=1&destination=${encodeURIComponent(job.site_address)}`}
                            target="_blank"
                            rel="noopener noreferrer"
                          >
                            <Button 
                              variant="outline" 
                              className="w-full h-12 text-[#194461] border-[#194461]/30 hover:bg-[#194461]/5"
                            >
                              <Navigation size={18} className="mr-2" />
                              Google Maps
                            </Button>
                          </a>
                          <a 
                            href={`https://maps.apple.com/?daddr=${encodeURIComponent(job.site_address)}`}
                            target="_blank"
                            rel="noopener noreferrer"
                          >
                            <Button 
                              variant="outline" 
                              className="w-full h-12 text-[#194461] border-[#194461]/30 hover:bg-[#194461]/5"
                            >
                              <ExternalLink size={18} className="mr-2" />
                              Apple Maps
                            </Button>
                          </a>
                        </div>

                        {job.notes && (
                          <div className="mb-4 p-3 bg-slate-50 rounded-lg text-sm text-slate-600">
                            <p className="font-medium text-slate-700 mb-1">Notes:</p>
                            {job.notes}
                          </div>
                        )}

                        {/* Photo Upload Section */}
                        <div className="mb-4">
                          <label className="text-sm font-medium text-slate-700 mb-2 block">
                            Add Photos
                          </label>
                          
                          {/* Photo Preview */}
                          {jobPhotos.length > 0 && (
                            <div className="flex flex-wrap gap-2 mb-3">
                              {jobPhotos.map((photoUrl, idx) => (
                                <div key={idx} className="relative w-20 h-20 rounded-lg overflow-hidden border border-slate-200">
                                  <img 
                                    src={`${BACKEND_URL}${photoUrl}`} 
                                    alt={`Service photo ${idx + 1}`}
                                    className="w-full h-full object-cover"
                                  />
                                  <button
                                    onClick={() => removePhoto(job.id, idx)}
                                    className="absolute top-1 right-1 w-5 h-5 bg-red-500 text-white rounded-full flex items-center justify-center"
                                  >
                                    <X size={12} />
                                  </button>
                                </div>
                              ))}
                            </div>
                          )}

                          {/* Upload Buttons */}
                          <div className="flex gap-2">
                            {/* Camera capture (mobile) */}
                            <label className="flex-1">
                              <input
                                type="file"
                                accept="image/*"
                                capture="environment"
                                onChange={(e) => handleCameraCapture(job.id, e)}
                                className="hidden"
                                data-testid={`camera-input-${job.id}`}
                              />
                              <Button
                                type="button"
                                variant="outline"
                                className="w-full h-12"
                                disabled={uploading === job.id}
                                asChild
                              >
                                <span>
                                  {uploading === job.id ? (
                                    <Loader2 size={18} className="animate-spin" />
                                  ) : (
                                    <Camera size={18} />
                                  )}
                                  <span className="ml-2">Take Photo</span>
                                </span>
                              </Button>
                            </label>

                            {/* File upload */}
                            <label className="flex-1">
                              <input
                                type="file"
                                accept="image/*"
                                multiple
                                onChange={(e) => handleFileSelect(job.id, e)}
                                className="hidden"
                                data-testid={`file-input-${job.id}`}
                              />
                              <Button
                                type="button"
                                variant="outline"
                                className="w-full h-12"
                                disabled={uploading === job.id}
                                asChild
                              >
                                <span>
                                  {uploading === job.id ? (
                                    <Loader2 size={18} className="animate-spin" />
                                  ) : (
                                    <Image size={18} />
                                  )}
                                  <span className="ml-2">Gallery</span>
                                </span>
                              </Button>
                            </label>
                          </div>

                          {jobPhotos.length > 0 && (
                            <p className="text-xs text-slate-500 mt-2">
                              {jobPhotos.length} photo{jobPhotos.length !== 1 ? 's' : ''} attached
                            </p>
                          )}
                        </div>

                        <div className="mb-4">
                          <label className="text-sm font-medium text-slate-700 mb-2 block">
                            Add notes (optional)
                          </label>
                          <Textarea
                            value={notes[job.id] || ''}
                            onChange={(e) => setNotes(prev => ({ ...prev, [job.id]: e.target.value }))}
                            placeholder="Any issues or comments..."
                            rows={2}
                            className="text-base"
                            data-testid={`notes-${job.id}`}
                          />
                        </div>

                        <div className="grid grid-cols-1 gap-3">
                          <Button
                            onClick={() => handleStatusUpdate(job, 'serviced')}
                            disabled={updating === job.id}
                            className="h-14 text-base bg-emerald-600 hover:bg-emerald-700"
                            data-testid={`btn-serviced-${job.id}`}
                          >
                            {updating === job.id ? (
                              <Loader2 className="h-5 w-5 animate-spin" />
                            ) : (
                              <>
                                <CheckCircle2 size={20} className="mr-2" />
                                Serviced
                              </>
                            )}
                          </Button>

                          <div className="grid grid-cols-2 gap-3">
                            <Button
                              onClick={() => openIssueDialog(job, 'not_accessible')}
                              disabled={updating === job.id}
                              variant="outline"
                              className="h-12 text-amber-600 border-amber-200 hover:bg-amber-50"
                              data-testid={`btn-not-accessible-${job.id}`}
                            >
                              <Ban size={18} className="mr-2" />
                              Not Accessible
                            </Button>

                            <Button
                              onClick={() => openIssueDialog(job, '')}
                              disabled={updating === job.id}
                              variant="outline"
                              className="h-12 text-red-600 border-red-200 hover:bg-red-50"
                              data-testid={`btn-issue-${job.id}`}
                            >
                              <AlertTriangle size={18} className="mr-2" />
                              Report Issue
                            </Button>
                          </div>
                        </div>
                      </div>
                    )}
                  </CardContent>
                </Card>
              );
            })}
          </div>
        )}
      </div>

      {/* Bottom navigation for mobile */}
      {stops.filter(s => s.status !== 'completed').length > 0 && (
        <div className="fixed bottom-0 left-0 right-0 bg-white border-t border-slate-200 p-4 shadow-lg">
          <div className="flex items-center justify-between gap-4">
            <div className="flex-shrink-0">
              <p className="text-xs text-slate-500">Remaining</p>
              <p className="text-lg font-bold text-slate-900">
                {stops.filter(s => s.status !== 'completed').length} stop{stops.filter(s => s.status !== 'completed').length !== 1 ? 's' : ''}
              </p>
            </div>
            <div className="flex-1 max-w-xs">
              <p className="text-xs text-slate-500 truncate mb-1">Next: {stops.find(s => s.status !== 'completed')?.customer_name}</p>
              <a 
                href={`https://www.google.com/maps/dir/?api=1&destination=${encodeURIComponent(stops.find(s => s.status !== 'completed')?.site_address || '')}`} 
                target="_blank" 
                rel="noopener noreferrer"
              >
                <Button className="w-full bg-[#194461] hover:bg-[#091D2E]" data-testid="navigate-next-btn">
                  <Navigation size={18} className="mr-2" />
                  Navigate to Next Stop
                </Button>
              </a>
            </div>
          </div>
        </div>
      )}

      {/* Issue Report Dialog */}
      <Dialog open={showIssueDialog} onOpenChange={setShowIssueDialog}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <AlertTriangle className="text-amber-500" size={20} />
              Report Issue
            </DialogTitle>
            <DialogDescription>
              {issueJob?.customer_name} - {issueJob?.site_address?.substring(0, 40)}...
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-4 py-4">
            <div>
              <Label htmlFor="issue-type">Issue Type *</Label>
              <Select value={issueType} onValueChange={setIssueType}>
                <SelectTrigger data-testid="issue-type-select">
                  <SelectValue placeholder="Select issue type" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="not_accessible">
                    <div className="flex items-center gap-2">
                      <Ban size={14} className="text-amber-500" />
                      Not Accessible
                    </div>
                  </SelectItem>
                  <SelectItem value="damaged_unit">
                    <div className="flex items-center gap-2">
                      <Wrench size={14} className="text-red-500" />
                      Damaged Unit
                    </div>
                  </SelectItem>
                  <SelectItem value="customer_complaint">
                    <div className="flex items-center gap-2">
                      <MessageSquareWarning size={14} className="text-orange-500" />
                      Customer Complaint
                    </div>
                  </SelectItem>
                  <SelectItem value="safety_hazard">
                    <div className="flex items-center gap-2">
                      <ShieldAlert size={14} className="text-red-600" />
                      Safety Hazard
                    </div>
                  </SelectItem>
                  <SelectItem value="other">
                    <div className="flex items-center gap-2">
                      <AlertTriangle size={14} className="text-slate-500" />
                      Other Issue
                    </div>
                  </SelectItem>
                </SelectContent>
              </Select>
            </div>

            <div>
              <Label htmlFor="issue-description">Description</Label>
              <Textarea
                id="issue-description"
                value={issueDescription}
                onChange={(e) => setIssueDescription(e.target.value)}
                placeholder="Describe the issue..."
                rows={3}
                data-testid="issue-description"
              />
            </div>

            {/* Issue Photos */}
            <div>
              <Label>Photos (optional)</Label>
              
              {issuePhotos.length > 0 && (
                <div className="flex flex-wrap gap-2 mt-2 mb-3">
                  {issuePhotos.map((photoUrl, idx) => (
                    <div key={idx} className="relative w-16 h-16 rounded-lg overflow-hidden border border-slate-200">
                      <img 
                        src={`${BACKEND_URL}${photoUrl}`} 
                        alt={`Issue photo ${idx + 1}`}
                        className="w-full h-full object-cover"
                      />
                      <button
                        onClick={() => removeIssuePhoto(idx)}
                        className="absolute top-0.5 right-0.5 w-4 h-4 bg-red-500 text-white rounded-full flex items-center justify-center"
                      >
                        <X size={10} />
                      </button>
                    </div>
                  ))}
                </div>
              )}

              <label className="block">
                <input
                  type="file"
                  accept="image/*"
                  capture="environment"
                  onChange={handleIssuePhotoCapture}
                  className="hidden"
                  data-testid="issue-photo-input"
                />
                <Button
                  type="button"
                  variant="outline"
                  className="w-full h-10"
                  asChild
                >
                  <span>
                    <Camera size={16} className="mr-2" />
                    Add Photo
                  </span>
                </Button>
              </label>
            </div>

            {/* Issue type specific warnings */}
            {issueType === 'damaged_unit' && (
              <div className="p-3 bg-red-50 border border-red-200 rounded-lg text-sm text-red-700">
                <p className="font-medium flex items-center gap-1">
                  <Wrench size={14} />
                  Unit will be flagged for maintenance
                </p>
                <p className="text-red-600 mt-1">
                  The affected unit(s) will be automatically removed from service rotation.
                </p>
              </div>
            )}

            {issueType === 'safety_hazard' && (
              <div className="p-3 bg-red-50 border border-red-200 rounded-lg text-sm text-red-700">
                <p className="font-medium flex items-center gap-1">
                  <ShieldAlert size={14} />
                  URGENT - Safety Issue
                </p>
                <p className="text-red-600 mt-1">
                  Dispatch will be immediately notified of this safety concern.
                </p>
              </div>
            )}
          </div>

          <DialogFooter className="flex-col sm:flex-row gap-2">
            <Button 
              variant="outline" 
              onClick={() => setShowIssueDialog(false)}
              className="w-full sm:w-auto"
            >
              Cancel
            </Button>
            <Button 
              onClick={handleSubmitIssue}
              disabled={submittingIssue || !issueType}
              className="w-full sm:w-auto bg-amber-600 hover:bg-amber-700"
              data-testid="submit-issue-btn"
            >
              {submittingIssue ? (
                <Loader2 className="h-4 w-4 animate-spin mr-2" />
              ) : (
                <AlertTriangle size={16} className="mr-2" />
              )}
              Submit Issue Report
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
